import { useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import parse from 'html-react-parser'
import { useTranslation } from 'next-i18next/pages'
import Lightbox from 'yet-another-react-lightbox'
import { useInView } from 'react-intersection-observer'
import 'yet-another-react-lightbox/styles.css'
import styles from './ProducerDetail.module.scss'
import { routes } from 'utils/routes'
import type { ProducerDetailProps } from './types'
import Tag from 'components/tag/Tag'
import ProducerSimulationBox from 'features/producer-detail/components/producer-simulation-box/ProducerSimulationBox'
import NewProducerMap from 'components/producers/new-producer-map/NewProducerMap'
import FeaturedArticle from 'components/article/featured-article/FeaturedArticle'
import { Button, Checkbox, Img, Accordion, Heading } from '@boltenergy-be/design-system'
import ProducerLocation from 'components/producers/producer-location/ProducerLocation'
import { getEnergyNameI18Key, getProducerLocation } from 'utils/producers'
import type { NewProducerMapProps } from 'components/producers/new-producer-map/types'
import NextLightboxImage from 'features/producer-detail/components/NextLightboxImage'
import ProducerSelectModal from 'features/producer-detail/components/producer-select-modal/ProducerSelectModal'
import { ProducerModalContext } from 'features/producer-detail/context'
import { ImageObject } from 'types/producer'
import { filterLocations, stripStyles } from './utils'
import { useStoreSelector } from 'hooks/store'
import useSelectInputGroup from 'hooks/useSelectInputGroup'
import { InputGroupKeys } from 'store/customer/enums'
import { useGetLanguage } from 'hooks/useGetLanguage'
import Link from 'components/link/Link'

const ProducerDetail = ({ isTablet, producer, relatedArticle, headerObjectPosition }: ProducerDetailProps) => {
  // Redux store
  const { banner } = useStoreSelector((store) => store.marketing)
  const { promoCode } = useSelectInputGroup(InputGroupKeys.SIMULATION)

  // I18n
  const { t } = useTranslation(['producer', 'producers', 'common'])
  const language = useGetLanguage()

  // Local state
  const [descriptionOpen, setDescriptionOpen] = useState<boolean>(false)
  const [extraInfoOpen, setExtraInfoOpen] = useState<boolean>(false)
  const [hideSoldOutLocations, setHideSoldOutLocations] = useState<boolean>(false)
  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false)
  const [lightboxIndex, setLightboxIndex] = useState(-1)
  const [producerModalOpen, setProducerModalOpen] = useState<boolean>(false)

  // Context
  const producerModalContext = useMemo(() => ({ producerModalOpen, setProducerModalOpen }), [producerModalOpen])

  // Ref
  const descriptionRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<HTMLDivElement>(null)

  // Intersection observer
  const [inViewRef, inView] = useInView({
    rootMargin: '-250px 0px -100px 0px',
    threshold: 0
  })

  // Constants
  const populatedLocations = useMemo<NewProducerMapProps['locations']>(
    () =>
      producer.locations.map((loc) => ({
        ...loc,
        id: producer.id,
        name: producer.name
      })),
    [producer.id, producer.locations, producer.name]
  )
  const filteredLocations = useMemo<NewProducerMapProps['locations']>(
    () => filterLocations(populatedLocations, hideSoldOutLocations),
    [hideSoldOutLocations, populatedLocations]
  )
  const mapCenter = useMemo<NewProducerMapProps['center']>(() => {
    const lat = Number(producer.locations[0]?.coordinates?.lat)
    const lng = Number(producer.locations[0]?.coordinates?.long)

    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : undefined
  }, [producer.locations])
  const producerExtraImages = useMemo<ImageObject['extra']>(
    () => (isTablet ? producer.images?.extra : producer.images?.extra?.slice(0, 2)),
    [isTablet, producer.images.extra]
  )
  const isBannerShown = !!banner

  /**
   * Hides the read more if the description is not long enough
   */
  useEffect(() => {
    if (descriptionRef.current) {
      const descriptionHeight = descriptionRef.current.clientHeight

      if (descriptionHeight < 600) {
        setDescriptionOpen(true)
      }
    }
  }, [])

  // Constants
  const { name, facts, longDescription } = producer
  const isParentProducer = producer.locations.length > 1
  const extraImagesCount = producer.images.extra?.length ?? 0
  const companyDescription = producer.companyDescription[language]
  const showReadMore = companyDescription.length > 200

  return (
    <>
      <main className={styles.main}>
        {/* HERO */}
        <section data-color="dark" className={styles['main-img-wrapper']}>
          <Img
            className={styles.image}
            priority
            publicId={producer.images.header?.publicId ?? ''}
            fill
            objectPosition={headerObjectPosition ?? undefined}
            alt={producer.name[language]}
            sizes="100vw"
          />
          <div className={classNames('container', styles.container, styles['producer-location-wrapper'])}>
            <ProducerLocation
              boxed
              className={styles.location}
              energyType={producer.locations[0].energyType}
              energyName={t(getEnergyNameI18Key(producer.locations[0].energyType), { ns: 'common' })}
              location={producer.locations.length === 1 ? getProducerLocation(language, producer.locations[0].location) : undefined}
            />
          </div>
        </section>

        {/* INFO */}
        <div className={classNames('container', styles.container)}>
          <div className={styles['back-container']}>
            <Link href={routes(language).producers} leadingIcon="chevronLeft">
              {t(($) => $.backCTA)}
            </Link>
          </div>
          <section className={styles['producer-info']}>
            <div className={styles.content}>
              <div className={styles['title-wrapper']}>
                <Heading as="h2" serif>
                  {name[language]}
                </Heading>
                {isParentProducer && (
                  <button
                    onClick={() => {
                      if (mapRef.current) {
                        window.scrollTo({ behavior: 'smooth', top: mapRef.current.offsetTop + mapRef.current.offsetHeight / 2 })
                      }
                    }}
                  >
                    <Tag>{`${producer.locations.length} ${t(($) => $.locations)}`}</Tag>
                  </button>
                )}
              </div>

              <div className={styles.highlights}>
                <div className={styles.wrapper}>
                  <Heading as="h3" variant="h5" className="mb-400">
                    {t(($) => $.highlightsTitle)}
                  </Heading>
                  <ul>
                    {facts?.[language].map((fact) => (
                      <li key={fact}>{fact}</li>
                    ))}
                  </ul>
                </div>
                {producer.images.logo && producer.companyDescription[language] && (
                  <div className={classNames(styles.wrapper, styles['extra-info'])}>
                    <Img height={50} width={100} publicId={producer.images.logo.publicId ?? ''} alt={producer.images.logo.name ?? ''} />
                    <div>
                      <p className={classNames({ [styles.open]: extraInfoOpen })}>{parse(companyDescription)}</p>
                      {showReadMore && !extraInfoOpen && (
                        <button onClick={() => setExtraInfoOpen(true)}>{t(($) => $.readMore, { ns: 'common' })}</button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className={classNames(styles['description-toggle'], { [styles.open]: descriptionOpen })}>
                <div ref={descriptionRef} className={styles.description}>
                  {parse(longDescription?.[language] ?? '', { replace: stripStyles })}
                </div>
                {!descriptionOpen && (
                  <Button className={styles.btn} onClick={() => setDescriptionOpen(true)}>
                    {t(($) => $.readMore, { ns: 'common' })}
                  </Button>
                )}
              </div>

              <div className={styles.gallery}>
                <picture
                  className={styles.profile}
                  style={!producer.images.extra?.length ? { gridColumn: '-1 / 1' } : {}}
                  onClick={() => {
                    setLightboxIndex(0)
                    setLightboxOpen(true)
                  }}
                >
                  <Img
                    fill
                    publicId={producer.images.profile?.publicId ?? ''}
                    alt={`${producer.name[language]} - ${producer.images.profile?.name}`}
                  />
                </picture>

                {!!producerExtraImages?.length &&
                  producerExtraImages.map((img, idx) => (
                    <picture
                      className={styles.extra}
                      key={idx}
                      onClick={() => {
                        setLightboxIndex(idx + 1)
                        setLightboxOpen(true)
                      }}
                    >
                      {!isTablet && extraImagesCount > 2 && idx === 1 && (
                        <div className={styles['extra-img-overlay']}>
                          <span>+ {extraImagesCount - 2}</span>
                        </div>
                      )}
                      <Img fill publicId={img.publicId ?? ''} alt={`${producer.name[language]} - ${img.name}`} />
                    </picture>
                  ))}
              </div>

              {lightboxOpen && (
                <Lightbox
                  open={true}
                  index={lightboxIndex}
                  close={() => setLightboxOpen(false)}
                  slides={[
                    { src: producer.images.profile?.publicId ?? '' },
                    ...(producer.images.extra?.length ? producer.images.extra?.map((img) => ({ src: img.publicId ?? '' })) : [])
                  ]}
                  render={{ slide: NextLightboxImage }}
                />
              )}
            </div>

            {/* SIDE CARD */}
            <aside ref={inViewRef} className={classNames(styles.aside)}>
              <div className={classNames(styles['aside-wrapper'], { [styles['extra-push-down']]: isBannerShown })}>
                <ProducerSimulationBox
                  openModal={() => setProducerModalOpen(true)}
                  highlighted
                  showPromoCode={!!promoCode?.code}
                  {...{ producer }}
                />

                <Accordion items={[{ title: t(($) => $.faq.producer.question), children: parse(t(($) => $.faq.producer.answer)) }]} />
              </div>
            </aside>
          </section>

          <div className={styles.hr} />

          <section className={styles.map}>
            <Heading as="h2" variant="d3">
              {t(($) => $.mapTitle)}
            </Heading>

            {isParentProducer && (
              <div className={styles['map-description']}>
                <p>{t(($) => $.map.description)}</p>
                <Checkbox
                  id="hideSoldOutLocations"
                  label={t(($) => $.map.hideSoldOut)}
                  name="hideSoldOutLocations"
                  checked={hideSoldOutLocations}
                  onChange={() => setHideSoldOutLocations(!hideSoldOutLocations)}
                />
              </div>
            )}

            <div ref={mapRef}>
              <NewProducerMap
                locations={filteredLocations}
                height="470px"
                zoom={11}
                center={mapCenter}
                enablePanning={!isTablet}
                fitBounds={populatedLocations.length > 1}
              />
            </div>
          </section>

          {relatedArticle && (
            <>
              <div className={styles.hr} />

              <section className={styles.news}>
                <Heading as="h2" variant="d3">
                  {t(($) => $.news.title)}
                </Heading>

                <FeaturedArticle asContentBlock article={relatedArticle} />
              </section>
            </>
          )}
        </div>
      </main>

      {isTablet && (
        <aside className={classNames(styles['mobile-simulation-box'], { [styles['hidden']]: inView })}>
          <ProducerSimulationBox
            openModal={() => setProducerModalOpen(true)}
            boxClass={styles['simulation-box']}
            showPromoCode={!!promoCode?.code}
            hide={{
              advantages: true,
              title: true
            }}
            {...{ producer }}
          />
        </aside>
      )}

      <ProducerModalContext.Provider value={producerModalContext}>
        <ProducerSelectModal
          isOpen={producerModalOpen}
          onRequestClose={() => setProducerModalOpen(false)}
          {...{ populatedLocations, producer }}
        />
      </ProducerModalContext.Provider>
    </>
  )
}

export default ProducerDetail
